import React from 'react';
import './FactionDetails.css';
import '../../player-profile/PlayerDetails/PlayerAccount/PlayerAccount.css';
import { useTranslations } from 'next-intl';
import ProfileDetailsContainer from '../../player-profile/ProfileDetailsContainer';
import ProfileDetailsBadge from '../../player-profile/PlayerDetails/ProfileDetailsBadge';
import ProfileDetailsCard from '../../player-profile/PlayerDetails/ProfileDetailsCard';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import type { FactionProfile } from '@wwe-portal/entities/faction.type';
import { useFactionDetails } from './useFactionDetails';

function FactionDetails({ faction }: Readonly<{ faction: FactionProfile }>) {
  const t = useTranslations();
  const details = useFactionDetails(t, faction);

  return (
    <div className="faction-details profile-details">
      {/* Mobile Version */}
      <div className="block md:hidden">
        <ProfileDetailsContainer>
          <div className="flex flex-col items-center justify-center">
            <div className="faction-account-container">
              <p className="text-md text-chetwodeBlue mb-[10px] text-center font-medium">
                {t('faction_name')}
              </p>
              <h4 className="text-inlineTextColor6 text-center text-xl font-semibold">
                {details.factionName}
              </h4>
              <div className="faction-account-details-container mt-[40px] flex flex-wrap">
                <div className="faction-account-details-item border-bayOfMany mb-[15px] flex w-full items-center justify-center border pr-[15px]">
                  <span className="text-deepSkyBlue faction-account-details-item-icon mr-1">
                    <IcoMoonIcon
                      iconName="account-group-fill"
                      className="size-[14px]"
                    />
                  </span>
                  <p className="text-text700 text-2xs m-0 mr-[10px] p-0">
                    {t('active_numbers')}
                  </p>
                  <p className="text-textColor3 text-2xs m-0 p-0">
                    {details.activeMemberCount}/100
                  </p>
                </div>
                <div className="faction-account-details-item border-bayOfMany mb-[15px] flex w-full items-center justify-center border pr-[15px]">
                  <span className="text-deepSkyBlue faction-account-details-item-icon mr-1">
                    <IcoMoonIcon
                      iconName="rewards-cup"
                      className="size-[14px]"
                    />
                  </span>
                  <p className="text-text700 text-2xs m-0 mr-[10px] p-0">
                    {t('last_feud_rank')}
                  </p>
                  <p className="text-textColor3 text-2xs m-0 p-0">
                    {details.lastFeudRank}
                  </p>
                </div>
                <div className="faction-account-details-item border-bayOfMany flex w-full items-center justify-center border">
                  <span className="text-deepSkyBlue faction-account-details-item-icon mr-1">
                    <IcoMoonIcon
                      iconName="calendar-skeleton"
                      className="size-[14px]"
                    />
                  </span>
                  <p className="text-text700 text-2xs m-0 mr-[10px] p-0">
                    {t('faction_created')}
                  </p>
                  <p className="text-textColor3 text-2xs m-0 p-0">
                    {details.createdDate}
                  </p>
                </div>
              </div>
            </div>
            <hr className="hr-profile my-[40px]" />
            <div className="flex w-full flex-col items-center">
              <ProfileDetailsBadge
                data={details.factionTier}
                title="Tier"
                bgImg="/assets/images/player/profile-badge_2.webp"
              />
              <div className="profile-details-item ml-0 mt-[30px] flex w-full flex-wrap">
                <div className="w-full px-5">
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="shild"
                        className="text-inlineTextColor11 size-6"
                      />
                    }
                    count={details.totalRosterTalent}
                    title={t('faction_total_roster_talent')}
                  />
                  <hr />
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="calendar-checked"
                        className="text-inlineTextColor11 size-6"
                      />
                    }
                    count={details.factionAge}
                    title={t('faction_age')}
                  />
                  <hr />
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="user-group-fill"
                        className="text-inlineTextColor11 size-6"
                      />
                    }
                    count={details.totalRosterSize}
                    title={t('faction_total_roster_size')}
                  />
                </div>
              </div>
            </div>
          </div>
        </ProfileDetailsContainer>
      </div>

      {/* Desktop Version */}
      <div className="hidden md:block">
        <ProfileDetailsContainer>
          <div className="flex justify-between gap-10">
            <div className="faction-account-container flex-1 sm:flex-[60%] xl:flex-1">
              <p className="text-text600 mb-[10px] text-sm font-medium">
                {t('faction_name')}
              </p>
              <h4 className="text-text500 text-xl font-semibold">
                {details.factionName}
              </h4>
              <div className="faction-account-details-container mt-10 grid max-w-[550px] grid-cols-2 gap-0 lg:grid-cols-3">
                <div className="faction-account-details-item border-r-bayOfMany mb-5 mr-5 flex items-center border-r pr-[15px]">
                  <span className="icon-account-group-fill border-bayOfMany text-blue700 mr-[10px] rounded-[10px] p-[10px]">
                    <IcoMoonIcon
                      iconName="account-group-fill"
                      className="size-[18px]"
                    />
                  </span>
                  <div>
                    <p className="text-text700 m-0 p-0 text-xs">
                      {t('active_numbers')}
                    </p>
                    <div className="text-text400 m-0 p-0 text-base font-medium">
                      {details.activeMemberCount}/100
                    </div>
                  </div>
                </div>
                <div className="faction-account-details-item border-r-bayOfMany mb-5 mr-5 flex items-center border-r pr-[15px]">
                  <span className="icon-account-group-fill border-bayOfMany text-blue700 mr-[10px] rounded-[10px] p-[10px]">
                    <IcoMoonIcon
                      iconName="rewards-cup"
                      className="size-[18px]"
                    />
                  </span>
                  <div>
                    <p className="text-text700 m-0 p-0 text-xs">
                      {t('last_feud_rank')}
                    </p>
                    <div className="text-text400 m-0 p-0 text-base font-medium">
                      {details.lastFeudRank}
                    </div>
                  </div>
                </div>
                <div className="faction-account-details-item mb-5 flex items-center">
                  <span className="icon-account-group-fill border-bayOfMany text-blue700 mr-[10px] rounded-[10px] p-[10px]">
                    <IcoMoonIcon
                      iconName="calendar-skeleton"
                      className="size-[18px]"
                    />
                  </span>
                  <div>
                    <p className="text-text700 m-0 p-0 text-xs">
                      {t('faction_created')}
                    </p>
                    <div className="text-text400 m-0 p-0 text-base font-medium">
                      {details.createdDate}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex max-w-[580px] items-center">
              <ProfileDetailsBadge
                data={details.factionTier}
                title="Tier"
                bgImg="/assets/images/player/profile-badge_2.webp"
                className="3xl:mr-7 mr-0"
              />
              <div className="profile-details-item 3xl:flex hidden flex-wrap">
                <div className="flex w-full">
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="shild"
                        className="text-green700 size-6"
                      />
                    }
                    count={details.totalRosterTalent}
                    title={t('faction_total_roster_talent')}
                  />
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="calendar-checked"
                        className="text-green700 size-6"
                      />
                    }
                    count={details.factionAge}
                    title={t('faction_age')}
                  />
                </div>
                <div className="flex w-full">
                  <ProfileDetailsCard
                    icon={
                      <IcoMoonIcon
                        iconName="user-group-fill"
                        className="text-green700 size-6"
                      />
                    }
                    count={details.totalRosterSize}
                    title={t('faction_total_roster_size')}
                  />
                </div>
                <hr />
              </div>

              <div className="profile-details-item 3xl:hidden ml-[5%] flex w-[170px] flex-col items-center 2xl:w-full">
                <ProfileDetailsCard
                  icon={
                    <IcoMoonIcon
                      iconName="calendar-skeleton"
                      className="text-green700 size-6"
                    />
                  }
                  count={details.factionAge}
                  title={t('faction_age')}
                />
                <ProfileDetailsCard
                  icon={
                    <IcoMoonIcon
                      iconName="shild"
                      className="text-green700 size-6"
                    />
                  }
                  count={details.totalRosterTalent}
                  title={t('faction_total_roster_talent')}
                />
                <ProfileDetailsCard
                  icon={
                    <IcoMoonIcon
                      iconName="user-group-fill"
                      className="text-green700 size-6"
                    />
                  }
                  count={details.totalRosterSize}
                  title={t('faction_total_roster_size')}
                />
              </div>
              <hr />
            </div>
          </div>
        </ProfileDetailsContainer>
      </div>
    </div>
  );
}

export default FactionDetails;
